import { useCallback, useEffect, useState } from "react";
import { FireServices } from "../../../shared/services/api/fire/FireServices";
import { mapFormDataToQuotationPayload } from "../../fire/quote/components/types/../utils/quotationPayloadMapper"; // Adjust path if needed
import { calculateCoveragesValues } from "../../fire/quote/components/types/../utils/calculateCoveragesValues"; // Adjust path if needed
import { FireQuotePayload, ClientCompanyData } from "../../fire/quote/components/types";
import { globalRemoveMaskReal2 } from "../../../shared/utils";

interface UseFireInsuranceQuotationProps {
    realEstateId?: string;
    clientId?: string;
    rentValue?: number;
    propertyType?: string;
    zipCode?: string;
    purpose?: number; // 1: Residential, 2: Commercial
    enabled?: boolean;
}

interface UseFireInsuranceQuotationResult {
    quoteValue: number | null;
    isLoading: boolean;
    error: string | null;
    isAllowed: boolean;
    hasFireModule: boolean;
}

export const useFireInsuranceQuotation = ({
    realEstateId,
    clientId,
    rentValue,
    propertyType,
    zipCode,
    purpose = 1,
    enabled = false,
}: UseFireInsuranceQuotationProps): UseFireInsuranceQuotationResult => {
    const [quoteValue, setQuoteValue] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isAllowed, setIsAllowed] = useState(false);
    const [hasFireModule, setHasFireModule] = useState(true);
    const [hasChecked, setHasChecked] = useState(false);

    const transformDateToISO = (dateString?: string): string | null => {
        if (!dateString) return null;
        try {
            return new Date(dateString).toISOString();
        } catch (e) {
            return new Date().toISOString();
        }
    };

    const fetchQuote = useCallback(async () => {


        if (!enabled) return;

        if (!realEstateId || !rentValue || !zipCode || !propertyType) {

            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            // 1. Get Companies
            const companiesResp = await FireServices.getAllCompanyById(realEstateId, 1, 10);
            const companies = companiesResp.data;

            if (!companies || companies.length === 0) {

                setIsAllowed(false);
                setHasFireModule(false);
                return;
            }

            const company = companies[0];
            const companyId = company.id;

            // 2. Get Details (Brokerage Commission, defaults) - Using getAllClientDataById to match FireQuote flow
            const clientDataResp = await FireServices.getAllClientDataById(realEstateId, companyId);
            const companyDetail = ("data" in clientDataResp ? clientDataResp.data?.[0] : clientDataResp) as ClientCompanyData;

            if (!companyDetail) {

                // Fallback or continue with risk? Better to warn.
            }

            // 3. Get Risk Groups to Map PropertyType -> Occupation
            const groupsResp = await FireServices.getGroupRisk();
            const groups = groupsResp.data || (Array.isArray(groupsResp) ? groupsResp : []);

            const group = groups.find((g: any) => String(g.id) === String(propertyType));

            if (!group) {
                // Fallback? Or fail? Failing is safer to avoid bad quotes.
                throw new Error("Tipo de imóvel não compatível com o seguro incêndio.");
            }

            // Select first occupation from the group
            const occupations = Array.isArray(group.ocupations) ? group.ocupations : [group.ocupations];
            if (!occupations || occupations.length === 0) {
                throw new Error("Nenhuma ocupação encontrada para este tipo de imóvel.");
            }
            const occupationId = occupations[0].id; // Default to first occupation

            // 4. Get Assistances
            const assistances = await FireServices.getAllAssistanceByOccupation(realEstateId, companyId, occupationId);
            const defaultAssistance = assistances.find((a: any) => a.ass_default === true || a.ass_default === "true") || assistances[0];
            const assistanceId = defaultAssistance?.id;

            // 5. Get Coverages
            const rawCoverages = await FireServices.getAllCoverageByOccupation(realEstateId, companyId, occupationId);

            // 6. Calculate Coverage Values (Apply Rent Multipliers/Limits)
            const calculatedCoverages = calculateCoveragesValues({
                coverages: rawCoverages.map((c: any) => ({
                    ...c,
                    factor: Number(c.factor ?? 1)
                })),
                rentValue: String(rentValue), // calculateCoveragesValues expects string usually or handle number? check util. It removes mask.
                quotationId: "novo" // treats as new quotation
            });

            // 7. Prepare Selection Data & Dependencies for Mapper
            const selectionData = {
                rentValue: String(rentValue), // Mapper removes mask, so string is fine
                occupationId: occupationId,
                assistanceId: assistanceId,
                predialPercent: companyDetail.pc_predio, // Use defaults from company details
                contentPercent: companyDetail.pc_conteudo
            };

            // Filter active local coverages for mapper (all enabled ones with value > 0)
            // The mapper takes `localCoverages` which looks like { id, value }.
            // `calculatedCoverages` has `value` as formatted string "R$ ...". Mapper cleans it.
            const localCoveragesForMapper = calculatedCoverages.map(c => ({
                id: c.id,
                value: c.value
            }));

            // Simulate Form Data that Mapper expects (dates, validity, etc.)
            const formData = {
                vigencyStart: new Date().toISOString(),
                vigencyMonths: 12,
                installments: companyDetail.installment_number ?? 1,
                prolabore_percent_amount: companyDetail?.default_prolabore_percent_amount || "0",
            };

            // Dummy total for dependency (mapper might use it for insurance_prize if logic dictates, but usually calculates it?)
            // Actually mapper assigns `insurance_prize: totalQuotation`.
            // Wait, we don't have the price yet. The API calculates it.
            // In FireQuotePresentation, `totalQuotation` is sum of coverages? No, `insuranceValues` is distinct.
            // Checking `FireQuotePresentation`:
            // `totalQuotation` passed to mapper seems to be the SUM of coverage values (if user edits them).
            // But wait, `insurance_prize` in payload is the OUTPUT price usually? 
            // In `mapFormDataToQuotationPayload`: `insurance_prize: prizeNormalized` (which is `totalQuotation`).
            // If this is a QUOTE REQUEST, we usually send 0 or the sum of coverages.
            // Let's sum the calculated coverage values for `totalQuotation`.
            const totalQuotation = localCoveragesForMapper.reduce((sum, c) => {
                return sum + Number(globalRemoveMaskReal2(String(c.value)));
            }, 0);

            const dependencies = {
                clientId, // The tenant ID
                companyId,
                selectionData,
                localCoverages: localCoveragesForMapper,
                companyData: companyDetail,
                totalQuotation: 0, // Requesting quote, so prize starts at 0? Or is it the sum of coverages? 
                // In `handleQuote` of FireQuotePresentation, `totalQuotation` is passed.
                // `totalQuotation` there is `activeCoverages.reduce(...)`.
                // So it is the sum of insured amounts (LMI).
                transformDateToISO,
                lastRentValue: null,
                brokerageId: companyDetail?.brokerage_code || null
            };

            // Redoing totalQuotation param for dependencies to match calculated sum
            // @ts-ignore
            dependencies.totalQuotation = totalQuotation;

            // 8. Map Payload
            const payload: FireQuotePayload = mapFormDataToQuotationPayload(formData, dependencies as any);

            console.log("PAYLOAD MOUNTED:", JSON.stringify(payload, null, 2));

            // 9. Send Quote
            const response = await FireServices.FireQuotation(payload);
            const data = response.data as any;
            const price = data.insurance_values ?? data.insurance_prize ?? data.total ?? 0;

            setQuoteValue(Number(price));
            setHasFireModule(true);
            setIsAllowed(true);

        } catch (err: any) {
            setError(err.message || "Failed to fetch quote");
            setIsAllowed(false);
        } finally {
            setIsLoading(false);
            setHasChecked(true);
        }
    }, [enabled, realEstateId, clientId, rentValue, zipCode, purpose, propertyType]);

    useEffect(() => {
        if (enabled && !hasChecked) {
            fetchQuote();
        }
    }, [fetchQuote, enabled, hasChecked]);

    return {
        quoteValue,
        isLoading,
        error,
        isAllowed,
        hasFireModule
    };
};
